import React, { useState, useEffect } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import api from '../services/api';
import { loadStripe } from '@stripe/stripe-js';
import SubscriptionChangeModal from './SubscriptionChangeModal';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const PricingTable = ({ currentTier = 'free' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [discountCode, setDiscountCode] = useState('');
  const [discountMessage, setDiscountMessage] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [paymentType, setPaymentType] = useState('subscription'); // Default to subscription

  console.log('PricingTable - currentTier prop received:', currentTier);

  useEffect(() => {
    detectPaymentType();
    fetchTiers();

    // Check for discount code in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('discount') || urlParams.get('promo');
    if (code) {
      setDiscountCode(code);
      setDiscountMessage(`Discount code "${code}" will be applied at checkout`);
    }
  }, []);

  const detectPaymentType = async () => {
    try {
      // Get subscription status to detect payment type
      const response = await api.get('/subscription/status');
      if (response.success && response.data?.subscription?.payment_type) {
        setPaymentType(response.data.subscription.payment_type);
      }
    } catch (err) {
      console.log('Could not detect payment type, defaulting to subscription');
      // Keep default subscription type
    }
  };

  const fetchTiers = async () => {
    try {
      setLoadingTiers(true);
      const response = await api.get('/subscription/tiers');
      if (response.success) {
        // Transform API data to component format and filter out invalid tiers
        // Allow one-time payments even with null price since they use Stripe Checkout pricing
        const transformedTiers = response.data
          .filter(tier => tier.priceId && (tier.price !== null || tier.interval === 'one_time'))
          .map((tier, index, availableTiers) => {
            const features = getTierFeatures(tier.key);
            
            // Determine which tier should be highlighted based on available tiers
            const shouldHighlight = availableTiers.length === 1 
              ? index === 0 // Single tier - highlight it
              : availableTiers.length === 2 
              ? index === 1 // Two tiers - highlight the second (higher)
              : availableTiers.length === 3
              ? index === 1 // Three tiers - highlight the middle one
              : tier.key === 'tier2'; // Four or more tiers - use tier2 if available
            
            const shouldShowBadge = shouldHighlight && currentTier !== tier.key;
            
            return {
              key: tier.key,
              name: tier.name,
              price: tier.price,
              currency: tier.currency || 'usd',
              period: tier.interval || 'month',
              features: features,
              cta: getCTAText(tier.key),
              highlighted: shouldHighlight,
              badge: shouldShowBadge ? 'Most Popular' : null
            };
          });
        setTiers(transformedTiers);
      }
    } catch (err) {
      console.error('Failed to fetch tiers:', err);
      setError('Failed to load pricing information');
    } finally {
      setLoadingTiers(false);
    }
  };

  const getTierFeatures = (tierKey) => {
    switch (tierKey) {
      // One-time payment tiers
      case 'single_report':
        return [
          { text: "1 Shopify store SEO analysis", included: true },
          { text: "All 100 action items visible", included: true },
          { text: "Comprehensive SEO audit", included: true },
          { text: "30-day access to results", included: true },
          { text: "Advanced recommendations", included: true },
          { text: "Export reports", included: true },
          { text: "Single-use purchase", included: true },
        ];
      case 'premium_report':
        return [
          { text: "1 Shopify store SEO analysis", included: true },
          { text: "All 100 action items visible", included: true },
          { text: "Comprehensive SEO audit", included: true },
          { text: "60-day access to results", included: true },
          { text: "Advanced recommendations", included: true },
          { text: "Export reports", included: true },
          { text: "Priority email support", included: true },
          { text: "Single-use purchase", included: true },
        ];
      // Subscription tiers
      case 'tier1':
        return [
          { text: "1 domain analysis per month", included: true },
          { text: "All 100 action items visible", included: true },
          { text: "Comprehensive SEO audit", included: true },
          { text: "Priority email support", included: true },
          { text: "Advanced recommendations", included: true },
          { text: "Export reports", included: true },
          { text: "Team collaboration", included: false },
        ];
      case 'tier2':
        return [
          { text: '3 domain analyses per month', included: true },
          { text: 'All 100 action items visible', included: true },
          { text: 'Comprehensive SEO audit', included: true },
          { text: 'Priority email support', included: true },
          { text: 'Advanced recommendations', included: true },
          { text: 'Export reports', included: true },
          { text: 'Team collaboration', included: false }
        ];
      case 'tier3':
        return [
          { text: '10 domain analyses per month', included: true },
          { text: 'All 100 action items visible', included: true },
          { text: 'Comprehensive SEO audit', included: true },
          { text: 'Priority phone & email support', included: true },
          { text: 'Advanced recommendations', included: true },
          { text: 'Export reports', included: true },
          { text: 'Team collaboration (coming soon)', included: false },
          { text: 'Custom integrations', included: true }
        ];
      case 'tier4':
        return [
          { text: '50 domain analyses per month', included: true },
          { text: 'All 100 action items visible', included: true },
          { text: 'Priority processing (faster results)', included: true },
          { text: 'Client portal access', included: true },
          { text: 'Beta features early access', included: true },
          { text: 'Premium phone & email support', included: true },
          { text: 'Advanced recommendations', included: true },
          { text: 'Export reports', included: true },
          { text: 'Team collaboration (coming soon)', included: false },
          { text: 'Custom integrations & API access', included: true },
          { text: 'White-label options', included: true }
        ];
      default:
        return [];
    }
  };

  const getCTAText = (tierKey) => {
    switch (tierKey) {
      // One-time payment CTAs
      case 'single_report':
        return 'Buy SEO Report';
      case 'premium_report':
        return 'Buy Premium Report';
      // Subscription CTAs
      case 'tier1':
        return 'Start Basic';
      case 'tier2':
        return 'Go Professional';
      case 'tier3':
        return 'Go Business';
      case 'tier4':
        return 'Go Power';
      default:
        return 'Select Plan';
    }
  };

  const formatPrice = (price, currency, interval) => {
    if (price === null || price === undefined) {
      // For one-time payments, show a more appropriate message
      if (interval === 'one_time') {
        return 'See checkout';
      }
      return 'Contact us';
    }
    // Format price based on currency
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return formatter.format(price);
  };

  const defaultTiers = [
    // Empty array - will be populated from API
  ];

  const handleSubscribe = async (tierKey) => {
    // Check if this is an upgrade/downgrade for existing subscription
    const isExistingSubscriber = currentTier !== 'free' && currentTier !== null;
    const isChangingPlan = isExistingSubscriber && tierKey !== currentTier;

    if (isChangingPlan) {
      // Show confirmation modal for plan changes
      setSelectedTier(tierKey);
      setModalOpen(true);
    } else {
      // Create new subscription checkout session (no modal needed)
      try {
        setLoading(true);
        setError(null);
        
        const checkoutData = {
          tier: tierKey,
          successUrl: `${window.location.origin}/account/subscription-success`,
          cancelUrl: `${window.location.origin}/account/upgrade`
        };
        
        // Add promotion code if available
        if (discountCode) {
          checkoutData.promotionCode = discountCode;
        }

        const response = await api.post('/subscription/create-checkout', checkoutData);

        if (response.success) {
          // Redirect to Stripe Checkout
          window.location.href = response.data.url;
        } else {
          throw new Error('Failed to create checkout session');
        }
      } catch (err) {
        setError(err.message || 'Failed to process subscription request. Please try again.');
        console.error('Subscription error:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleConfirmChange = async () => {
    try {
      setLoading(true);
      setError(null);

      // Handle subscription update (upgrade/downgrade)
      const updateData = {
        newTier: selectedTier,
        currentTier: currentTier,
        isUpgrade: !isDowngrade(selectedTier)
      };

      const response = await api.post('/subscription/update-plan', updateData);

      if (response.success) {
        // Show success message and reload
        const action = isDowngrade(selectedTier) ? 'downgrade' : 'upgrade';
        const timing = isDowngrade(selectedTier) 
          ? 'Your downgrade will take effect at the end of your current billing period.'
          : 'Your upgrade has been applied immediately.';
        
        alert(`Subscription ${action} successful! ${timing}`);
        window.location.reload();
      } else {
        throw new Error(response.error || 'Failed to update subscription');
      }
    } catch (err) {
      setError(err.message || 'Failed to process subscription change. Please try again.');
      console.error('Subscription error:', err);
    } finally {
      setLoading(false);
      setModalOpen(false);
      setSelectedTier(null);
    }
  };

  const getButtonText = (tier) => {
    if (loading) return 'Processing...';

    // For one-time payments
    if (paymentType === 'one_time') {
      if (currentTier === tier.key) return 'Access Active';
      return `Purchase ${tier.name}`;
    }

    // For subscriptions
    if (currentTier === tier.key) return 'Current Plan';

    // For existing subscribers, show clear upgrade/downgrade text
    if (currentTier !== 'free' && currentTier !== null) {
      if (isDowngrade(tier.key)) {
        return `Downgrade to ${tier.name}`;
      } else {
        return `Upgrade to ${tier.name}`;
      }
    }

    // For new subscribers
    return tier.cta;
  };

  const isDowngrade = (tierKey) => {
    const tierOrder = { 'free': 0, 'tier1': 1, 'tier2': 2, 'tier3': 3, 'tier4': 4 };
    return tierOrder[tierKey] < tierOrder[currentTier];
  };

  if (loadingTiers) {
    return (
      <div className="bg-card p-8">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-muted-foreground">Loading pricing information...</span>
        </div>
      </div>
    );
  }

  if (!tiers.length && !loadingTiers) {
    return (
      <div className="bg-card p-8">
        <div className="text-center">
          <p className="text-muted-foreground">Unable to load pricing information at this time.</p>
          <button 
            onClick={fetchTiers}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {discountMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {discountMessage}
          </p>
        </div>
      )}
      
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <div className={`grid gap-3 ${
        tiers.length === 1 
          ? 'grid-cols-1 max-w-sm mx-auto'
          : tiers.length === 2 
          ? 'grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto'
          : tiers.length === 3
          ? 'grid-cols-1 md:grid-cols-3 max-w-4xl mx-auto'
          : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
      }`}>
        {tiers.map((tier) => (
          <div
            key={tier.key}
            className={`relative rounded-2xl bg-card ${
              tier.highlighted
                ? 'border-2 border-blue-500 shadow-xl'
                : 'border border-border'
            } p-4`}
          >
            {(currentTier === tier.key || (tier.badge && currentTier !== tier.key)) && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  currentTier === tier.key 
                    ? 'bg-green-600 text-white'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                }`}>
                  {currentTier === tier.key ? 'Current Plan' : tier.badge}
                </span>
              </div>
            )}

            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-foreground mb-2">{tier.name}</h3>
              <div className="flex items-baseline justify-center">
                <span className="text-5xl font-extrabold text-foreground">
                  {formatPrice(tier.price, tier.currency, tier.period)}
                </span>
                <span className="text-muted-foreground ml-2">/{tier.period}</span>
              </div>
            </div>

            <ul className="space-y-4 mb-8">
              {tier.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  {feature.included ? (
                    <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  ) : (
                    <XMarkIcon className="h-5 w-5 text-gray-300 mt-0.5 mr-3 flex-shrink-0" />
                  )}
                  <span className={`text-sm ${feature.included ? 'text-card-foreground' : 'text-gray-400'}`}>
                    {feature.text}
                  </span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSubscribe(tier.key)}
              disabled={loading || currentTier === tier.key}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                currentTier === tier.key
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : isDowngrade(tier.key)
                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                  : tier.highlighted
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
                  : 'bg-gray-800 text-white hover:bg-gray-900'
              }`}
            >
              {getButtonText(tier)}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center text-sm text-muted-foreground">
        <p className="mt-2 mb-2">Cancel or change your plan anytime</p>
      </div>

      {/* Subscription Change Modal */}
      <SubscriptionChangeModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedTier(null);
        }}
        onConfirm={handleConfirmChange}
        currentPlan={currentTier}
        newPlan={selectedTier}
        currentPlanPrice={tiers.find(t => t.key === currentTier)?.price}
        newPlanPrice={tiers.find(t => t.key === selectedTier)?.price}
        isUpgrade={selectedTier && !isDowngrade(selectedTier)}
        loading={loading}
      />
    </div>
  );
};

export default PricingTable;